/**
 * Structured log events — US 21.04.
 *
 * One shape on both sides of the wire: the client's transport layer and the
 * Edge Functions emit the same JSON lines, correlated on `actionId`. When a
 * trade goes wrong in production, the `actionId` from the player's error
 * report finds every hop of that request in one search.
 *
 * This module is pure — it builds and serialises events. Delivery (console,
 * Sentry breadcrumb, server stdout) belongs to the caller.
 */

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

export interface LogEvent {
  ts: string; // ISO 8601
  level: LogLevel;
  msg: string;
  /** Correlation id — the `actionId` of the request this event belongs to. */
  actionId?: string;
  sessionId?: string;
  /** Free-form context. Keys must not collide with the fields above. */
  ctx?: Record<string, unknown>;
}

const RESERVED = new Set(['ts', 'level', 'msg', 'actionId', 'sessionId', 'ctx']);

export interface LogEventInput {
  level: LogLevel;
  msg: string;
  actionId?: string;
  sessionId?: string;
  ctx?: Record<string, unknown>;
  /** Injectable clock so tests are deterministic. Defaults to Date.now. */
  now?: () => number;
}

/**
 * Builds a log event. Throws on a reserved key in `ctx` rather than silently
 * shadowing a field — a `ctx.actionId` that disagrees with the real one would
 * poison the correlation search precisely when it is needed.
 */
export function makeLogEvent(input: LogEventInput): LogEvent {
  if (input.ctx) {
    for (const key of Object.keys(input.ctx)) {
      if (RESERVED.has(key)) {
        throw new Error(`log ctx key "${key}" shadows a structured field`);
      }
    }
  }
  const event: LogEvent = {
    ts: new Date((input.now ?? Date.now)()).toISOString(),
    level: input.level,
    msg: input.msg,
  };
  if (input.actionId !== undefined) event.actionId = input.actionId;
  if (input.sessionId !== undefined) event.sessionId = input.sessionId;
  if (input.ctx !== undefined && Object.keys(input.ctx).length > 0) event.ctx = input.ctx;
  return event;
}

/**
 * Serialises for transport/stdout. Values that JSON cannot represent
 * (undefined, functions, cycles) must not crash logging — a logger that can
 * throw turns every log site into a potential fault.
 */
export function serialiseLogEvent(event: LogEvent): string {
  try {
    return JSON.stringify(event);
  } catch {
    return JSON.stringify({
      ts: event.ts,
      level: event.level,
      msg: event.msg,
      ctx: { serialisation: 'failed' },
    });
  }
}
